/**
 * Inline error copy for Position write actions (MDP-608).
 *
 * Positions have no name uniqueness, so this module deliberately never returns
 * the org-object name-in-use copy. Callers pass the action's verb so a 403
 * names the action the admin was trying to perform.
 */

const PERMISSION_MESSAGES = {
  rename: "You don't have permission to rename this Position.",
  archive: "You don't have permission to archive this Position.",
  unarchive: "You don't have permission to unarchive this Position.",
  capacity: "You don't have permission to change this Position's capacity.",
};

export const POSITION_NOT_FOUND_MESSAGE =
  "This Position no longer exists. Refresh the page.";
export const POSITION_GENERIC_ERROR_MESSAGE = "Something went wrong. Try again.";

function flattenMessages(value, messages = []) {
  if (value == null || value === "") return messages;
  if (typeof value === "string" || typeof value === "number") {
    const text = String(value).trim();
    if (text) messages.push(text);
    return messages;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => flattenMessages(item, messages));
    return messages;
  }
  if (typeof value === "object") {
    // Prefer DRF's conventional keys before walking arbitrary field errors.
    ["detail", "message", "error", "name"].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        flattenMessages(value[key], messages);
      }
    });
    Object.entries(value).forEach(([key, item]) => {
      if (["detail", "message", "error", "name"].includes(key)) return;
      flattenMessages(item, messages);
    });
  }
  return messages;
}

/** The server's field/validation message, or "" when the response has none. */
export function extractPositionServerMessage(error) {
  const data = error?.response?.data;
  const messages = [...new Set(flattenMessages(data))];
  return messages.join(" ").trim();
}

function resolveStatus(error) {
  const status = error?.response?.status ?? error?.status;
  const numeric = Number(status);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Map a Position action failure to the inline dialog message.
 *
 * @param {object} error Axios/network error.
 * @param {"rename"|"archive"|"unarchive"|"capacity"} action The action being attempted.
 * @returns {string}
 */
export function positionActionErrorMessage(error, action = "rename") {
  const status = resolveStatus(error);
  if (status === 403) {
    return PERMISSION_MESSAGES[action] || PERMISSION_MESSAGES.rename;
  }
  if (status === 404) return POSITION_NOT_FOUND_MESSAGE;
  if (status === 400) {
    return extractPositionServerMessage(error) || POSITION_GENERIC_ERROR_MESSAGE;
  }
  return POSITION_GENERIC_ERROR_MESSAGE;
}
